// Pure, probe-clean leaf for people-name matching + the who-merge map.
//
// ZERO runtime imports (string-only logic) so scripts/probe-people-alias.mjs
// can load it under `tsx` via a relative path, the way the Bible-search
// probes load their pure leaves. The alias-suggestion precision logic
// (couldBeSamePerson / isContainmentMatch), the candidate-eligibility gate
// (isAliasCandidateEligible), and the merge-map reducer (applyMerge) live
// here so the probe exercises the SAME code the store + prayer-utils run,
// not a reimplementation.
//
// Extracted byte-faithfully from prayer-utils.ts + settings-store.ts in P0 of
// docs/plans/people-alias-suggestion-audit-2026-06-08.md. See CLAUDE.md
// rule #30 (four-layer duplicate-people prevention) + rule #31 (read-time
// merge via canonicalizeWho, never mutating parsed.who).

import type { Prayer } from '../types/prayer';

export const SELF_KEY = 'Self';
export const GENERAL_KEY = 'General';

export function isGeneral(value: string | null | undefined): boolean {
  return value?.trim().toLocaleLowerCase('en-US') === GENERAL_KEY.toLocaleLowerCase('en-US');
}

export function isSelf(who: string): boolean {
  return who.trim().toLowerCase() === 'self';
}

/** Read-time entry type: never changes stored words, dates or person membership. */
export function isPraise(prayer: Prayer): boolean {
  return isSelf(prayer.parsed?.who?.trim() || GENERAL_KEY) &&
    (prayer.status ?? 'Open') === 'Answered';
}

/**
 * Who merge map. Keys are lowercased/trimmed aliases ("my mother", "mama",
 * "mother"); values are the canonical display name the user chose ("Mom").
 * Applied at read time via applyCanonical so merges never touch a prayer's
 * stored parsed data, which makes merge fully reversible (rule #31).
 */
export type WhoMergeMap = Record<string, string>;

/** Lowercase + trim: the canonical alias-key form for the merge map. */
export function normalizeAlias(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Apply the merge map to a raw display name. Returns the canonical form if
 * the alias has been merged; otherwise the name unchanged. Null/empty
 * passthrough so callers don't need guards. Pure: the map is passed in
 * (the store-aware default lives in settings-store's canonicalizeWho wrapper).
 *
 * OWN-PROPERTY-ONLY (CR, PC-B rev 9 section 15 decision 6): the lookup
 * consults only an OWN string-valued property via Object.hasOwn. An
 * inherited `constructor`, `toString`, or `__proto__` value is never a
 * mapping; a non-string own value falls through to the raw name.
 */
export function applyCanonical(name: string, map: WhoMergeMap): string {
  if (!name) return name;
  const key = normalizeAlias(name);
  if (Object.hasOwn(map, key)) {
    const v = map[key];
    if (typeof v === 'string') return v;
  }
  return name;
}

/**
 * Pure who-merge reducer. Points each alias key at `canonical`, then
 * collapses transitive chains so resolution stays single-hop (a -> c, not
 * a -> b -> c where b was just merged into c). Returns the SAME map
 * reference unchanged when `canonical` is empty (the store action treats
 * an unchanged reference as a no-op, preserving prior behavior); otherwise
 * returns a fresh object.
 */
export function applyMerge(
  map: WhoMergeMap,
  aliases: string[],
  canonical: string,
): WhoMergeMap {
  // Malformed guard (CR, PC-B rev 9 section 12 item 3): a non-string
  // canonical is a no-op; non-string aliases are skipped below.
  if (typeof canonical !== 'string') return map;
  const canonicalTrimmed = canonical.trim();
  if (!canonicalTrimmed) return map;
  // Sentinel guard (CR, PC-B rev 9 section 12 item 3 + executed failure
  // case 4): the reserved protocol roots — exactly `Self` and `General`
  // for protocol v1, at ANY casing — can never be written as a merge
  // target, even if a surface is missed now or added later. Localized
  // labels (You, Tú, Você, Geral) are display values, not roots, and are
  // NOT blocked here.
  const canonicalRootKey = whoKey(canonicalTrimmed);
  if (canonicalRootKey === 'self' || canonicalRootKey === 'general') return map;
  const canonicalKey = normalizeAlias(canonicalTrimmed);
  // Null-prototype working map so an alias key like "__proto__" creates an
  // ordinary own property instead of mutating the prototype (CR: raw
  // derivation and every merge write use a null-prototype temporary map).
  const next: WhoMergeMap = Object.create(null) as WhoMergeMap;
  for (const [k, v] of Object.entries(map)) next[k] = v;
  for (const alias of aliases) {
    if (typeof alias !== 'string') continue;
    const aliasKey = normalizeAlias(alias);
    // Skip empty, self-mapping, an alias equal to the canonical name
    // (which would be a no-op that just clutters the map), and the
    // reserved roots at any casing (a sentinel is never an alias).
    if (!aliasKey || aliasKey === canonicalKey) continue;
    const aliasRootKey = whoKey(alias);
    if (aliasRootKey === 'self' || aliasRootKey === 'general') continue;
    next[aliasKey] = canonicalTrimmed;
  }
  // Collapse transitive chains at write time: if an existing key pointed to
  // something that's now in `aliases`, re-point it to the new canonical.
  for (const [k, v] of Object.entries(next)) {
    if (normalizeAlias(v) === canonicalKey) continue;
    const vKey = normalizeAlias(v);
    if (aliases.some((a) => typeof a === 'string' && normalizeAlias(a) === vKey)) {
      next[k] = canonicalTrimmed;
    }
  }
  return next;
}

/**
 * Normalized key for a `who`: lowercase, strip leading/trailing punctuation,
 * collapse whitespace, and drop a leading "my "/"our " when followed by
 * >= 2 chars (guarded so a name like "My" isn't normalized to empty).
 */
export function whoKey(who: string): string {
  let k = who.trim().toLowerCase();
  k = k.replace(/^[.,;:!?'"()-]+|[.,;:!?'"()-]+$/g, '');
  k = k.replace(/\s+/g, ' ');
  k = k.replace(/^(?:my|our)\s+(?=.{2,})/, '');
  return k.trim();
}

/**
 * Normalized Damerau-free Levenshtein for short name comparison. Simple
 * DP, good enough for names (max ~30 chars). Returns raw edit distance
 * so callers can compare it against a length-aware threshold.
 *
 * Deliberately a separate copy from topic-search.ts's levenshtein and from
 * edit-distance.ts's OSA variant (CLAUDE.md: do not consolidate the three).
 */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const prev: number[] = new Array(b.length + 1);
  const curr: number[] = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + cost,
      );
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j];
  }
  return prev[b.length];
}

// A token is "possessive" when it ends in an apostrophe-s or a bare trailing
// apostrophe (straight ' or curly ’): "mom's", "boys'". whoKey preserves
// internal apostrophes (it only strips leading/trailing punctuation), so
// "Mom's coworker" -> "mom's coworker" keeps the possessive marker.
const POSSESSIVE_TOKEN = /['’]s?$/;

/**
 * Whole-token, possessive-aware containment between two normalized keys
 * (P3). True when the shorter key (>= 3 chars) equals a whole NON-possessive
 * whitespace token of the longer key. So "Ann" matches "Ann Marie" and "Tom"
 * matches "Pastor Tom", but "Mom" does NOT match "Mom's coworker" (the only
 * candidate token "mom's" is possessive, a relational modifier for a
 * different person). The >= 3 guard stops "Al" matching "Alexander".
 *
 * Shared by couldBeSamePerson AND findAliasCandidate's ranking so the
 * "contains-first" rank order can never drift from what actually matched.
 */
export function isContainmentMatch(ka: string, kb: string): boolean {
  const short = ka.length <= kb.length ? ka : kb;
  const long = ka.length <= kb.length ? kb : ka;
  if (short.length < 3 || short === long) return false;
  for (const tok of long.split(/\s+/)) {
    if (POSSESSIVE_TOKEN.test(tok)) continue;
    if (tok === short) return true;
  }
  return false;
}

/**
 * Heuristic: could these two canonical names plausibly refer to the same
 * person? The parser + existingPeople context prevents most duplicates at
 * source and the normalizer catches mechanical variants, so this is a
 * last-resort, precision-biased pass for the rare residuals ("Pastor T" vs
 * "Pastor Tom", a typo like "Sara" vs "Sarah").
 *
 * Rules (ANY triggers true):
 *   - Whole-token, possessive-aware containment (isContainmentMatch).
 *   - Length-adaptive Levenshtein with a short-name floor: distance <= 1 for
 *     4-6 char names, <= 2 for 7-10, <= 3 for longer. Keys <= 3 chars are
 *     containment-only (no distance match), so Mom/Tom, Dad/Dan, Ann/Ana,
 *     Jon/Jan, Sam/Pam are NOT flagged.
 *
 * Returns false when either name is empty, identical, or shares the same
 * normalized key (those are already one person).
 *
 * Kinship variants like "Mama" vs "Mom" are deliberately NOT caught here
 * (distance 2, no containment): Layer 1 (the parser, CLAUDE.md rule #29)
 * canonicalizes kinship to Mom/Dad/Brother at source.
 */
export function couldBeSamePerson(a: string, b: string): boolean {
  const ka = whoKey(a);
  const kb = whoKey(b);
  if (!ka || !kb || ka === kb) return false;
  // (i) Whole-token, possessive-aware containment.
  if (isContainmentMatch(ka, kb)) return true;
  // (ii) Length-adaptive Levenshtein with a >= 4 short-name floor.
  const maxLen = Math.max(ka.length, kb.length);
  if (maxLen >= 4) {
    const threshold = maxLen <= 6 ? 1 : maxLen <= 10 ? 2 : 3;
    if (levenshtein(ka, kb) <= threshold) return true;
  }
  return false;
}

/**
 * Stable pair key for the dismissed-suggestions set. Sorted + joined with a
 * rare delimiter so the same two names always map to the same key regardless
 * of which was the "target" when the suggestion fired.
 */
export function aliasPairKey(a: string, b: string): string {
  return [whoKey(a), whoKey(b)].sort().join('␟');
}

/**
 * Is `candidate` eligible to be SUGGESTED as an alias of another person?
 * Excludes the reserved sentinels: Self (a protocol literal, not a person)
 * and General (the abstract bucket). Used by findAliasCandidate to keep the
 * sentinels out of the candidate list even though couldBeSamePerson can
 * match them at the pairwise level (e.g. "Sela" vs "Self", distance 1). Wired in P2.
 */
export function isAliasCandidateEligible(candidate: string): boolean {
  return !isSelf(candidate) && candidate !== GENERAL_KEY;
}

// ---------------------------------------------------------------------------
// Shared explicit-map identity helpers (CR, PC-B rev 9 section 15 decision 6)
// ---------------------------------------------------------------------------
//
// These are the STORE-FREE pure helpers every identity consumer delegates
// to: the store-aware client helpers in prayer-utils, Remember's mirror in
// remember-moments, the legacy-grouping notice, and the server parity copy
// in api/_lib/keepsake/identity.ts. They take an EXPLICIT safe map and
// perform no Zustand read. Under CR the active map is empty, so passing
// `emptyIdentityMap()` yields the baseline grouping by construction.

/** A fresh null-prototype empty identity map (the CR baseline). */
export function emptyIdentityMap(): WhoMergeMap {
  return Object.create(null) as WhoMergeMap;
}

/**
 * Primary display name for a prayer under an EXPLICIT map. Sentinels pass
 * through unmapped (exact-string comparison, matching the historical
 * `whoOf`; the sentinel normalization sweep is S5, not CR).
 */
export function whoOfWithMap(prayer: Prayer, map: WhoMergeMap): string {
  const raw = prayer.parsed?.who?.trim() || GENERAL_KEY;
  if (raw === GENERAL_KEY || raw === SELF_KEY) return raw;
  return applyCanonical(raw, map);
}

/**
 * Every distinct named person referenced in a single prayer under an
 * EXPLICIT map, as display strings with FIRST-SEEN casing, deduplicated by
 * `whoKey`. Excludes a General primary; includes a Self primary. This is
 * the single membership implementation (server mirror:
 * api/_lib/keepsake/identity.ts); no consumer keeps a private fork.
 */
export function peopleInPrayerWithMap(prayer: Prayer, map: WhoMergeMap): string[] {
  const byKey = new Map<string, string>();
  const primary = whoOfWithMap(prayer, map);
  if (primary && primary !== GENERAL_KEY) {
    byKey.set(whoKey(primary), primary);
  }
  for (const name of prayer.parsed?.mentionedPeople ?? []) {
    if (typeof name !== 'string') continue;
    const trimmed = name.trim();
    if (!trimmed) continue;
    const canonical = applyCanonical(trimmed, map);
    const key = whoKey(canonical);
    if (!byKey.has(key)) byKey.set(key, canonical);
  }
  return Array.from(byKey.values());
}
